import { useRef, useCallback, useEffect } from 'react';
import { CrudForm } from '../../../containers/crud/crudForm';
import { HtmlPreview } from '../../../containers/htmlPreview';
import { BlockEditor } from '../../../containers/blockEditor';
import { ALPHANUMERIC_HYPENS } from '@arivaa-react/components/regex';
import Tags from '@arivaa-react/components/tags';
import { Row, Col } from 'antd';
import { useApiWithSideEffects } from '../../../../hooks/api';
import { url } from '../../../../constants/api';
import { translate } from '../../../../localization';

/**
 * @description PDF Template Form
 * @type Container
 * @author Inderdeep
 */

const Main = (props) => {
  let { data } = props;
  const form = useRef(null);
  const editorRef = useRef();

  if (data) {
    data = { ...data, ...data.metadata };
  }
  const spinnerSelector = '.ant-form';

  const {
    data: pdfTemplates,
    callApi: getPdfTemplates,
  } = useApiWithSideEffects({
    spinnerSelector,
  });
  const {
    data: emailTemplates,
    callApi: getEmailTemplates,
  } = useApiWithSideEffects({
    spinnerSelector,
  });

  const onClickHandler = useCallback((variable, replaceAllText) => {
    if (replaceAllText === true) {
      editorRef.current.setContent('');
      editorRef.current.execCommand('mceInsertContent', false, variable + ' ');
      return;
    }
    editorRef.current.execCommand('mceInsertContent', false, variable + ' ');
  }, []);

  const onTagsClick = useCallback((value) => {
    onClickHandler(`{{${value}}}`, false);
  }, []);
  const onTemplateClick = useCallback((value) => {
    onClickHandler(value, true);
  }, []);

  useEffect(() => {
    getPdfTemplates(url('html-template'), {
      params: { search: 'type;pdf', all: true },
    });

    getEmailTemplates(url('html-template'), {
      params: { search: 'type;email', all: true },
    });
  }, []);
  return (
    <CrudForm
      {...props}
      skipTranslate={true}
      ref={form}
      renderForm={({ renderElement }) => {
        return (
          <div className="template-builder">
            <Row>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <div className="form">
                  <div className="inline">
                    {renderElement({
                      type: 'text',
                      name: 'code',
                      label: translate('pdfTemplate.key.label'),
                      required: true,
                      options: {
                        rules: [
                          {
                            pattern: ALPHANUMERIC_HYPENS,
                            message:
                              'Code can only be alphanumeric and can contain only hyphens (-)',
                          },
                        ],
                      },
                      inputProps: {
                        disabled: !!data,
                        className: 'uppercase',
                      },
                    })}
                    {renderElement({
                      type: 'select',
                      name: 'relatedTemplateId',
                      label: 'Email Template',
                      required: false,
                      data: (emailTemplates || []).map(({ key, code }) => {
                        return {
                          label: code,
                          value: key,
                        };
                      }),
                    })}
                    {renderElement({
                      type: 'text',
                      name: 'name',
                      label: translate('pdfTemplate.name.label'),
                      required: true,
                    })}
                  </div>
                  {renderElement({
                    type: 'custom',
                    name: 'templateVariables',
                    label: 'Template Variables',
                    Component: Tags,
                    required: false,
                    inputProps: {
                      tagProps: {
                        onClick: (evt) => onTagsClick(evt.target.innerText),
                      },
                      allowNewTag: true,
                      editable: true,
                    },

                    after: (
                      <div className="templates-container">
                        <p className="label">Choose Template:</p>
                        <div className="templates">
                          {(pdfTemplates || []).map((item, i) => {
                            if (item.isSystem) {
                              return (
                                <HtmlPreview
                                  key={i}
                                  className={'template'}
                                  onClick={onTemplateClick}
                                  value={item}
                                />
                              );
                            }
                          })}
                        </div>
                      </div>
                    ),
                  })}
                  {renderElement({
                    type: 'switch',
                    name: 'isSystem',
                    label: 'System Template',
                    required: false,
                  })}
                </div>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <div className="preview">
                  {renderElement({
                    type: 'custom',
                    name: 'template',
                    label: translate('template.template.label'),
                    required: true,
                    Component: BlockEditor,
                    inputProps: {
                      editorProps: {
                        onInit: (evnt, editor) => (editorRef.current = editor),
                      },
                    },
                  })}
                </div>
              </Col>
            </Row>
          </div>
        );
      }}
      data={data}
      preSubmit={(values) => {
        values.type = 'pdf';
        const metadata = ['name', 'relatedTemplateId'];
        values.metadata = {};
        metadata.forEach((ele) => {
          //pick the values from data in case of update since they are undefined if no changes were made
          values.metadata[ele] = values[ele] || data?.[ele];
          delete values[ele];
        });

        if (!data) {
          values.code = values?.code?.toUpperCase();
        }
        return values;
      }}
    />
  );
};

Main.displayName = 'Pdf-Template-Form';

export default Main;
